import { apiService } from './api';
import { unwrapResponse } from './apiUtils';
import {
  FarmPlot,
  CreateFarmPlotDto,
  UpdateFarmPlotDto,
  FarmPlotListRequest,
  FarmPlotListResponse
} from '../types/farmPlots';
import { EntityDependencySummary } from '../types/entityDependencies';
import { DuplicateMatch } from '../types/duplicateMatch';

export const farmPlotService = {
  // Get paginated farm plots with search and filtering
  getFarmPlots: async (request: FarmPlotListRequest): Promise<FarmPlotListResponse> => {
    const params = new URLSearchParams();

    if (request.pageNumber) params.append('pageNumber', request.pageNumber.toString());
    if (request.pageSize) params.append('pageSize', request.pageSize.toString());
    if (request.searchTerm) params.append('searchTerm', request.searchTerm);
    if (request.idFarm) params.append('idFarm', request.idFarm.toString());
    if (request.idFarmer) params.append('idFarmer', request.idFarmer.toString());
    if (request.sortBy) params.append('sortBy', request.sortBy);
    if (request.sortDescending !== undefined) params.append('sortDescending', request.sortDescending.toString());

    const response = await apiService.get<FarmPlotListResponse>(`/farmplots?${params.toString()}`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch farm plots');
    }

    return response.data;
  },

  // Get farm plot by ID
  getById: async (id: number): Promise<FarmPlot> => {
    const response = await apiService.get<FarmPlot>(`/farmplots/${id}`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch farm plot');
    }

    return response.data;
  },

  // Create new farm plot
  create: async (data: CreateFarmPlotDto): Promise<FarmPlot> => {
    const response = await apiService.post<FarmPlot>('/farmplots', data);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to create farm plot');
    }

    return response.data;
  },

  // Update farm plot
  update: async (id: number, data: UpdateFarmPlotDto): Promise<FarmPlot> => {
    const response = await apiService.put<FarmPlot>(`/farmplots/${id}`, data);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to update farm plot');
    }

    return response.data;
  },

  // Delete farm plot
  delete: async (id: number): Promise<void> => {
    const response = await apiService.delete<void>(`/farmplots/${id}`);

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete farm plot');
    }
  },

  // Delete pre-check: what references this plot + whether it can be deleted (server is authoritative).
  getDependencies: async (id: number): Promise<EntityDependencySummary> => {
    const response = await apiService.get<EntityDependencySummary>(`/farmplots/${id}/dependencies`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to check field dependencies');
    }

    return response.data;
  },

  // Search farm plots
  search: async (searchTerm: string): Promise<FarmPlot[]> => {
    const response = await apiService.get<FarmPlot[]>(`/farmplots/search?searchTerm=${encodeURIComponent(searchTerm)}`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to search farm plots');
    }

    return response.data;
  },

  // Get farm plots by farm ID (for dropdowns/selectors)
  getByFarm: async (farmId: number): Promise<FarmPlot[]> => {
    const response = await apiService.get<FarmPlotListResponse>(`/farmplots?idFarm=${farmId}&pageSize=1000`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch farm plots');
    }

    return response.data.items;
  },

  // Create-time "did you mean?" nudge (Plan C, Layer 1): plots under this farm matching the in-progress name.
  // Non-blocking — see useDuplicateCheck / DuplicateMatchPanel. Query built from defined params only.
  checkDuplicates: async (params: { farmId: number; name: string; excludeId?: number }): Promise<DuplicateMatch[]> => {
    const query = new URLSearchParams();
    query.set('farmId', String(params.farmId));
    query.set('name', params.name);
    if (params.excludeId != null) query.set('excludeId', String(params.excludeId));

    const response = await apiService.get<DuplicateMatch[]>(`/farmplots/check-duplicates?${query.toString()}`);
    return unwrapResponse(response, 'Failed to check for duplicate plots');
  },
};
